import React, { useEffect } from 'react';
import type { PageProps } from '../types';
import { AccessibilityChecker } from '../components/AccessibilityChecker';
import { TimeoutError } from '../components/TimeoutError';
import { useAuditStore } from '../stores/auditStore';
import { OverviewBackground } from '../components/icons/OverviewBackground';

export const OverviewPage: React.FC<PageProps> = ({ onNavigate }) => {
  const { auditResult, auditError, auditInProgress, setAuditError, setAuditResult } = useAuditStore();

  // Show analyzing when content script is running an audit or when we have no result yet and no error
  const isAnalyzing = auditInProgress || (auditResult === null && !auditError);

  // When we have results and audit is not in progress, clear error and navigate to results
  useEffect(() => {
    if (auditResult && !auditInProgress) {
      setAuditError(null);
      onNavigate('accessibilityIssueHeading');
    }
  }, [auditResult, auditInProgress, onNavigate, setAuditError]);

  // Handle retry (Scan again) - runs audit for currently selected device only
  const handleRetry = () => {
    setAuditError(null);
    setAuditResult(null);

    if (window.webyesAuditUtils) {
      const auditFunction = window.webyesAuditUtils.runAuditWithPreparation;
      if (auditFunction) {
        auditFunction()
          .then((results: unknown) => {
            if (results) {
              setAuditResult(results);
              setAuditError(null);
            }
          })
          .catch((error: unknown) => {
            console.error('[WebYes Checker] Retry audit failed:', error);
            const errorMessage = (error instanceof Error ? error.message : String(error));
            if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
              setAuditError('timeout');
            } else {
              setAuditError(errorMessage);
            }
            setAuditResult(buildMergedOrNull());
          });
      } else {
        setAuditError('Audit utilities not available');
      }
    } else {
      setAuditError('Audit utilities not available');
    }
  };

  function buildMergedOrNull(): unknown {
    const utils = (window as unknown as { webyesAuditUtils?: { buildMergedAuditResult?: () => unknown } }).webyesAuditUtils;
    if (utils && typeof utils.buildMergedAuditResult === 'function') {
      return utils.buildMergedAuditResult();
    }
    return null;
  }

  // Show timeout error screen
  if (auditError) {
    return (
      <div className="cy:relative cy:flex cy:flex-col cy:gap-6 cy:px-4 cy:py-6 cy:min-h-[500px]">
        <div className="cy:relative cy:z-10">
          <TimeoutError onRetry={handleRetry} />
        </div>
      </div>
    );
  }

  return (
    <div className="cy:relative cy:flex cy:flex-col cy:gap-6 cy:px-4 cy:py-6 cy:min-h-[500px]">
     
      
      {/* Content */}
      <div className="cy:relative cy:z-10">
        <AccessibilityChecker
          onAnalyze={() => {}} // No manual trigger needed
          isAnalyzing={isAnalyzing}
          auditResult={auditResult}
          onOpenResults={() => onNavigate('accessibilityIssueHeading')}
        />
      </div>
       <div className=" cy:flex cy:items-center cy:justify-center cy:pointer-events-none ">
        <OverviewBackground width={400} height={400} />
      </div>
    </div>
  );
};

